/**
 * Finance > Invoices > detail. The invoice PDF on the left (embedded viewer
 * with the browser's native toolbar), the editable fields on the right.
 *
 * Editable: vendor, invoice number, invoice date, due date, total, currency,
 * subtotal, tax, PO number, GL code. Read-only: file name, date received,
 * mailbox, matched vendor (the email-derived facts). Saving sets the fields and
 * locks the row so the automated extractor will not overwrite the manual
 * correction. A locked row stays EDITABLE here: the lock guards automation, not
 * human edits (the lock tooltip says so).
 *
 * Layout: the PDF column is sticky so it does not add to page scroll, and the
 * field forms use a condensed two-column grid. The page-level actions (open the
 * PDF in a new tab, cockpit-only Notes, Not an invoice) live as a compact icon
 * group in the header, mirroring the grid row's icon group, instead of as tall
 * stacked cards.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { useSession } from '@/lib/useSession';
import { formatDate, formatDateTime } from '@/lib/dateUtils';
import { LockIndicator } from '@/components/LockIndicator';
import { OpenDocumentButton, DismissRecordButton } from '@/components/RowActionIcons';
import { InvoiceNotesPopover } from './InvoiceNotesPopover';
import {
  combinedInvoiceStatus,
  combinedStatusLabel,
  formatInvoiceTotal,
  hasDocumentsWrite,
  invoicePdfUrl,
  useInvoiceDetail,
  useEditInvoice,
  useSetInvoiceErp,
  useMarkNotInvoice,
  type EditInvoiceBody,
  type InvoiceDetail as InvoiceDetailType,
} from '@/lib/useInvoices';
import { MatchedVendor, VendorSource } from './VendorPopup';

export function InvoiceDetail() {
  const { documentId } = useParams<{ documentId: string }>();
  const [searchParams] = useSearchParams();
  const backSearch = searchParams.toString();
  const { user } = useSession();
  const canWrite = hasDocumentsWrite(user?.permissions, user?.superAdmin);
  const navigate = useNavigate();

  const detailQ = useInvoiceDetail(documentId);
  const edit = useEditInvoice();
  const markNotInvoice = useMarkNotInvoice();

  const backHref = { pathname: '/finance/invoices', search: backSearch ? `?${backSearch}` : '' };

  const onNotInvoice = () => {
    if (!documentId) return;
    markNotInvoice.mutate(
      { documentId },
      { onSuccess: () => navigate(backHref) },
    );
  };

  const detail = detailQ.data;

  return (
    <div className="flex h-full min-h-0 flex-col pb-4">
      <Link
        to={backHref}
        className="text-sm text-amp-ink-muted hover:text-amp-ink inline-flex items-center gap-1 mb-2 dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
      >
        <ArrowLeft className="h-3 w-3" /> Back to invoices
      </Link>

      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark">
            Finance / Invoices
          </p>
          <h1 className="truncate text-xl font-semibold text-amp-ink dark:text-amp-dark-ink">
            {detail?.invoice_number || detail?.vendor || detail?.document_name || 'Invoice'}
          </h1>
        </div>
        {detail && documentId && (
          <div className="flex shrink-0 items-center gap-3">
            <InvoiceNotesPopover documentId={documentId} notesCount={0} canWrite={canWrite} eager />
            <OpenDocumentButton url={invoicePdfUrl(documentId)} label="Open PDF in new tab" />
            {canWrite && (
              <DismissRecordButton
                confirmMessage="Mark this document as not an invoice? It will be removed from the list. This inactivates the document and does not delete anything."
                onConfirm={onNotInvoice}
                disabled={markNotInvoice.isPending}
              />
            )}
          </div>
        )}
      </div>

      {detailQ.isLoading ? (
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading...</p>
      ) : detailQ.error ? (
        <p className="text-sm text-red-700 dark:text-red-300">Could not load: {detailQ.error.message}</p>
      ) : detail ? (
        <>
          {markNotInvoice.error && (
            <p className="mb-3 text-xs text-red-700 dark:text-red-300">{markNotInvoice.error.message}</p>
          )}
          <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 lg:grid-cols-2">
            {/* PDF column is sticky so it does not add to page scroll. */}
            <div className="lg:sticky lg:top-0 lg:self-start">
              <PdfViewer documentId={documentId ?? ''} />
            </div>
            <div className="space-y-4">
              <FieldsPanel
                detail={detail}
                canWrite={canWrite}
                saving={edit.isPending}
                onSave={(body) =>
                  edit.mutate(
                    { documentId: documentId ?? '', body },
                    { onError: () => undefined },
                  )
                }
                saveError={edit.error?.message ?? null}
                saved={edit.isSuccess}
              />
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

function PdfViewer({ documentId }: { documentId: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let revoked: string | null = null;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    fetch(invoicePdfUrl(documentId), { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new Error(`${res.status} ${text.slice(0, 200)}`);
        }
        const blob = await res.blob();
        if (cancelled) return;
        const objUrl = URL.createObjectURL(blob);
        revoked = objUrl;
        setUrl(objUrl);
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [documentId]);

  // Cap the embed to the viewport height so a tall PDF does not stretch the
  // page; the embedded viewer scrolls internally. The Open PDF icon opens the
  // full document in a new tab for a larger read.
  return (
    <div className="flex h-[calc(100vh-180px)] min-h-[400px] flex-col overflow-hidden rounded-lg border border-amp-line bg-amp-white dark:border-amp-dark-line dark:bg-amp-dark-surface">
      {loading ? (
        <div className="flex flex-1 items-center justify-center text-amp-ink-muted dark:text-amp-dark-ink-muted">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : err ? (
        <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          Could not load the PDF ({err}). The invoice may not be in SharePoint yet.
        </div>
      ) : url ? (
        <iframe title="Invoice PDF" src={url} className="w-full flex-1" />
      ) : null}
    </div>
  );
}

// Sage AP_OpenInvoice.Comment is VARCHAR(40). The api enforces the cap on write;
// the input enforces it here too (maxLength + a live counter).
const COMMENT_MAX = 40;

// Trim a string field to null when empty so blanks are stored as NULL.
function strOrNull(v: string): string | null {
  const t = v.trim();
  return t === '' ? null : t;
}

// Parse a numeric input to a number, or null when blank / not a number.
function numOrNull(v: string): number | null {
  const t = v.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

// Plain-English note for an auto-filled GL, shown under the GL field.
function glSourceHint(source: string, confidencePct?: number | null): string {
  if (source === 'card') return 'Auto-filled from the vendor’s default GL account.';
  if (source === 'history_common') {
    return confidencePct != null
      ? `Auto-filled from this vendor’s history (${confidencePct}% of past invoices used this GL).`
      : 'Auto-filled from this vendor’s history.';
  }
  if (source === 'history_recent') return 'Suggested from this vendor’s most recent invoice.';
  return 'Auto-filled from vendor history.';
}

function FieldsPanel({
  detail,
  canWrite,
  saving,
  onSave,
  saveError,
  saved,
}: {
  detail: InvoiceDetailType;
  canWrite: boolean;
  saving: boolean;
  onSave: (body: EditInvoiceBody) => void;
  saveError: string | null;
  saved: boolean;
}) {
  const seed = useMemo(
    () => ({
      vendor: detail.vendor ?? '',
      invoice_number: detail.invoice_number ?? '',
      invoice_date: detail.invoice_date ?? '',
      due_date: detail.due_date ?? '',
      total_amount: detail.total_amount != null ? String(detail.total_amount) : '',
      currency: detail.currency ?? '',
      subtotal: detail.subtotal != null ? String(detail.subtotal) : '',
      tax: detail.tax != null ? String(detail.tax) : '',
      po_number: detail.po_number ?? '',
      gl_code: detail.gl_code ?? '',
      comment: detail.comment ?? '',
    }),
    [detail],
  );
  const [form, setForm] = useState(seed);

  // Re-seed local fields when the loaded document changes.
  useEffect(() => {
    setForm(seed);
  }, [seed]);

  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const submit = () => {
    onSave({
      vendor: strOrNull(form.vendor),
      invoice_number: strOrNull(form.invoice_number),
      invoice_date: strOrNull(form.invoice_date),
      due_date: strOrNull(form.due_date),
      total_amount: numOrNull(form.total_amount),
      currency: strOrNull(form.currency),
      subtotal: numOrNull(form.subtotal),
      tax: numOrNull(form.tax),
      po_number: strOrNull(form.po_number),
      gl_code: strOrNull(form.gl_code),
      comment: strOrNull(form.comment),
    });
  };

  // The api sets gl_locked once a GL code is saved. Reflect that lock in the UI
  // (the field stays editable, but we show a lock hint so it is clear the value
  // is now protected from automated overwrites). Tolerant of number/boolean.
  const glLocked = !!detail.gl_locked;

  const inputCls =
    'w-full border border-amp-line rounded px-2.5 py-1 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink';
  const labelCls = 'block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-0.5';

  // ERP switcher: shown only when the matched vendor exists in more than one ERP
  // (an overlap vendor). Switching records a vendor+account-number rule so future
  // invoices on that account auto-route. See useSetInvoiceErp.
  const setErp = useSetInvoiceErp();
  const erpOptions = detail.erp_options ?? [];
  const canSwitchErp = canWrite && erpOptions.length > 1;
  const erpLabel = (e: string) => (e === 'sage' ? 'Sage' : e === 'bc' ? 'BC' : e);

  return (
    <div className="space-y-4">
      {/* Email-derived facts: condensed two-column read-only grid. */}
      <div className="border border-amp-line rounded-lg bg-amp-white p-3 dark:border-amp-dark-line dark:bg-amp-dark-surface">
        <h2 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-amp-ink dark:text-amp-dark-ink">
          Invoice details
          <LockIndicator locked={detail.locked} />
        </h2>
        <dl className="grid grid-cols-1 gap-x-4 gap-y-1.5 text-sm sm:grid-cols-2">
          <ReadRow label="Status">
            {combinedStatusLabel(combinedInvoiceStatus(detail))}{detail.locked ? ' (manually edited)' : ''}
          </ReadRow>
          <ReadRow label="Date received">{formatDateTime(detail.email_date)}</ReadRow>
          <ReadRow label="File name">{detail.document_name}</ReadRow>
          <ReadRow label="From">{detail.from_address ?? '--'}</ReadRow>
          <ReadRow label="Matched vendor">
            <MatchedVendor fields={detail} extractedVendor={detail.vendor} />
          </ReadRow>
          {detail.matched_vendor_erp && (
            <ReadRow label="Source">
              <span className="inline-flex items-center gap-1.5">
                <VendorSource erp={detail.matched_vendor_erp} />
                {detail.vendor_match_score != null && (
                  <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">
                    Match: {Math.round(detail.vendor_match_score)}%
                    {detail.vendor_match ? ` (${detail.vendor_match})` : ''}
                  </span>
                )}
              </span>
            </ReadRow>
          )}
          {canSwitchErp && (
            <ReadRow label="ERP">
              <div className="flex flex-col gap-1">
                <select
                  value={detail.matched_vendor_erp ?? ''}
                  disabled={setErp.isPending}
                  onChange={(e) =>
                    setErp.mutate({ documentId: detail.document_id, erp: e.target.value as 'sage' | 'bc' })
                  }
                  className={`${inputCls} max-w-[160px]`}
                >
                  {erpOptions.map((e) => (
                    <option key={e} value={e}>{erpLabel(e)}</option>
                  ))}
                </select>
                <span className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  {detail.account_number
                    ? `Future invoices on account ${detail.account_number} will route to ${erpLabel(detail.matched_vendor_erp ?? '')}.`
                    : 'Applies to this invoice only (no account number to remember).'}
                </span>
                {setErp.isError && (
                  <span className="text-xs text-red-700 dark:text-red-300">{setErp.error.message}</span>
                )}
              </div>
            </ReadRow>
          )}
          {detail.erp_balance != null && (
            <ReadRow label="Owed">
              <span className="text-amp-ink dark:text-amp-dark-ink">
                {formatInvoiceTotal(detail.erp_balance, detail.currency)}
              </span>
            </ReadRow>
          )}
          <ReadRow label="Mailbox">{detail.mailbox ?? '--'}</ReadRow>
          {detail.last_edited_by && (
            <ReadRow label="Last edited">{detail.last_edited_by} &middot; {formatDateTime(detail.last_edited_at)}</ReadRow>
          )}
        </dl>
      </div>

      {/* Editable extracted fields: condensed two-column form. */}
      <div className="border border-amp-line rounded-lg bg-amp-white p-3 dark:border-amp-dark-line dark:bg-amp-dark-surface">
        <h2 className="mb-2 text-sm font-semibold text-amp-ink dark:text-amp-dark-ink">
          {canWrite ? 'Review and correct' : 'Extracted fields'}
        </h2>
        {canWrite ? (
          <div className="space-y-2.5">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Vendor</label>
                <input type="text" value={form.vendor} onChange={(e) => set('vendor', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Invoice number</label>
                <input type="text" value={form.invoice_number} onChange={(e) => set('invoice_number', e.target.value)} className={inputCls} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Invoice date</label>
                <input type="date" value={form.invoice_date} onChange={(e) => set('invoice_date', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Due date</label>
                <input type="date" value={form.due_date} onChange={(e) => set('due_date', e.target.value)} className={inputCls} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Total amount</label>
                <input type="number" step="0.01" value={form.total_amount} onChange={(e) => set('total_amount', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Currency</label>
                <input type="text" value={form.currency} onChange={(e) => set('currency', e.target.value)} placeholder="USD" className={inputCls} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Subtotal</label>
                <input type="number" step="0.01" value={form.subtotal} onChange={(e) => set('subtotal', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Tax</label>
                <input type="number" step="0.01" value={form.tax} onChange={(e) => set('tax', e.target.value)} className={inputCls} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>PO number</label>
                <input type="text" value={form.po_number} onChange={(e) => set('po_number', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>
                  <span className="inline-flex items-center gap-1">
                    GL code
                    {glLocked && <LockIndicator locked={glLocked} />}
                  </span>
                </label>
                <input
                  type="text"
                  value={form.gl_code}
                  onChange={(e) => set('gl_code', e.target.value)}
                  placeholder="e.g. 6000-100"
                  className={`${inputCls} font-mono`}
                  aria-label="GL code"
                />
                {detail.gl_account_desc && (
                  <p className="mt-1 text-[11px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
                    {detail.gl_account_desc}
                  </p>
                )}
                {!glLocked && detail.gl_source && (
                  <p className="mt-1 text-[11px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
                    {glSourceHint(detail.gl_source, detail.gl_confidence_pct)}
                  </p>
                )}
              </div>
            </div>
            <p className="text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
              Saving the GL code locks it from automated overwrites. You can still edit it here.
            </p>
            {saveError && <p className="text-xs text-red-700 dark:text-red-300">{saveError}</p>}
            {saved && !saveError && (
              <p className="text-xs text-green-700 dark:text-green-300">Saved. Locked for re-processing.</p>
            )}
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              className="inline-flex items-center gap-1 text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1.5 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
            </button>
          </div>
        ) : (
          <dl className="grid grid-cols-1 gap-x-4 gap-y-1.5 text-sm sm:grid-cols-2">
            <ReadRow label="Vendor">{detail.vendor ?? '--'}</ReadRow>
            <ReadRow label="Invoice number">{detail.invoice_number ?? '--'}</ReadRow>
            <ReadRow label="Invoice date">{formatDate(detail.invoice_date)}</ReadRow>
            <ReadRow label="Due date">{formatDate(detail.due_date)}</ReadRow>
            <ReadRow label="Total amount">{detail.total_amount != null ? String(detail.total_amount) : '--'}</ReadRow>
            <ReadRow label="Currency">{detail.currency ?? '--'}</ReadRow>
            <ReadRow label="Subtotal">{detail.subtotal != null ? String(detail.subtotal) : '--'}</ReadRow>
            <ReadRow label="Tax">{detail.tax != null ? String(detail.tax) : '--'}</ReadRow>
            <ReadRow label="PO number">{detail.po_number ?? '--'}</ReadRow>
            <ReadRow label="GL code">
              <span className="inline-flex items-center gap-1.5">
                <span className="font-mono">
                  {detail.gl_code
                    ? detail.gl_account_desc
                      ? `${detail.gl_code}: ${detail.gl_account_desc}`
                      : detail.gl_code
                    : '--'}
                </span>
                <LockIndicator locked={glLocked} />
              </span>
            </ReadRow>
          </dl>
        )}
      </div>

      {/* The single Sage-synced Comment (distinct from the cockpit-only Notes).
          The Comment is the Sage AP_OpenInvoice.Comment field and does nothing
          for BC / Ramp / unmatched invoices, so it is shown only for Sage-matched
          rows. The cockpit-only Notes (the notes icon in the header) stay visible
          for every invoice. */}
      {detail.matched_vendor_erp === 'sage' && (
      <div className="border border-amp-olive/40 rounded-lg bg-amp-cream-warm p-3 dark:border-amp-olive-dark/40 dark:bg-amp-dark-surface-warm">
        <h2 className="text-sm font-semibold text-amp-ink dark:text-amp-dark-ink">Comment (synced to Sage)</h2>
        <p className="mb-2 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
          A single 40-character note carried into the Sage AP import. Separate from cockpit-only Notes (the notes icon above).
        </p>
        {canWrite ? (
          <div className="space-y-2">
            <input
              type="text"
              value={form.comment}
              maxLength={COMMENT_MAX}
              onChange={(e) => set('comment', e.target.value.slice(0, COMMENT_MAX))}
              placeholder="Optional Sage comment"
              className={inputCls}
              aria-label="Sage comment"
            />
            <div className="flex items-center justify-between">
              <span className="text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
                {form.comment.length}/{COMMENT_MAX}
              </span>
              <button
                type="button"
                onClick={submit}
                disabled={saving}
                className="inline-flex items-center gap-1 text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1.5 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface"
              >
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save comment
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-amp-ink dark:text-amp-dark-ink break-words">{detail.comment ?? '--'}</p>
        )}
      </div>
      )}
    </div>
  );
}

function ReadRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="w-28 shrink-0 text-amp-ink-muted dark:text-amp-dark-ink-muted">{label}</dt>
      <dd className="flex-1 min-w-0 text-amp-ink dark:text-amp-dark-ink break-words">{children}</dd>
    </div>
  );
}
